import { type ModeConfig, type SeriesFormat } from '../lib/modes';

// ─────────────────────────────────────────────────────────────────────────────
// SeriesFormatEditor — the Bo1/Bo3/Bo5/Bo7/Bo9 length picker + per-game
// mode-order selects + cost callout.
//
// T3 (per-stage config reshape): extracted from CompetitionCreateForm's
// original comp-level-only SeriesEditor/CostCallout pair so the SAME
// markup/logic drives BOTH the comp-level "Series Format" block (legacy,
// no-stages path) AND each composed stage's own per-stage series format
// block (StagePipelinePanel.tsx's StageConfigFields) — defined ONCE, used in
// both places. `modes` is always the comp-level mode DEFINITIONS
// (Hardpoint/S&D/Overload etc. — see frontend/src/lib/modes.ts): per the
// product spec, modes are defined once at comp level; only the SERIES
// (length/order/wins) varies per stage. Pure/presentational — no hooks, no
// network calls.
// ─────────────────────────────────────────────────────────────────────────────

// CostCallout removed (Lisa, 2026-07-19): the "Cost factor: 1.6× a Bo3 …
// pick deliberately" banner moved OFF the stage editor and into the pricing
// copy (app How It Works + site pricing) — series length driving read count
// is pricing information, not a per-stage warning. SERIES_COST_MULTIPLIER
// stays in lib/modes.ts as the single source for that copy.

export function SeriesFormatEditor({ modes, series, onChange }: { modes: ModeConfig[]; series: SeriesFormat; onChange: (s: SeriesFormat) => void }) {
  const setLength = (len: number) => {
    const order: string[] = [];
    for (let i = 0; i < len; i++) order.push(series.modeOrder[i] ?? modes[i % modes.length]?.key ?? '');
    onChange({ length: len, modeOrder: order, winsNeeded: Math.ceil(len / 2) });
  };

  const setSlot = (i: number, key: string) => {
    const next = [...series.modeOrder];
    next[i] = key;
    onChange({ ...series, modeOrder: next });
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
        <label className="label shrink-0">Series length</label>
        <div className="flex flex-wrap gap-1">
          {[1, 3, 5, 7, 9].map(n => (
            <button key={n} type="button" onClick={() => setLength(n)}
              className={`rounded px-2.5 py-1 text-xs font-bold transition-colors ${series.length === n ? 'bg-violet-600 text-white' : 'bg-surface-700 text-gray-400 hover:text-white'}`}>
              Bo{n}
            </button>
          ))}
        </div>
        <span className="text-xs text-gray-500">First to {series.winsNeeded} wins</span>
      </div>
      <div className="flex flex-wrap gap-2">
        {series.modeOrder.map((key, i) => (
          <div key={i} className="flex items-center gap-1">
            <span className="text-[10px] text-gray-600 font-mono w-4">G{i + 1}</span>
            <select value={key} onChange={e => setSlot(i, e.target.value)}
              className="input text-xs py-1 px-2">
              {modes.map(m => <option key={m.key} value={m.key}>{m.shortLabel}</option>)}
            </select>
            {i < series.modeOrder.length - 1 && <span className="text-gray-600 text-xs">→</span>}
          </div>
        ))}
      </div>
    </div>
  );
}
